import { type GeoLocation, type GeoLocationType } from '@smg-real-estate/geo-libs';

/** Re-evaluated location types for new Agency/Agent SRPs (PP-4136). */
const NEW_SRP_INDEXABLE_LOCATION_TYPES: GeoLocationType[] = [
  'place',
  'region',
  'district',
  'canton',
  'citydistrict',
];

export type NewSrpRobotsContent = 'noindex, follow' | 'noindex, nofollow';

export interface NewSrpIndexationInput {
  locations: GeoLocation[] | null | undefined;
  pageQuery?: unknown;
  totalCount: number;
}

export interface NewSrpIndexationResult {
  isIndexable: boolean;
  isIndexableForJsonLd: boolean;
  robotsContent: NewSrpRobotsContent | null;
  fetchPage: number;
}

export function isNewSrpLocationTypeIndexable(type: GeoLocationType): boolean {
  return NEW_SRP_INDEXABLE_LOCATION_TYPES.includes(type);
}

/**
 * Parses the raw `page` query for new SRPs.
 * Invalid values are not indexable; fetch still falls back to page 1.
 */
export function parseDirectoryPageQuery(pageQuery: unknown): {
  fetchPage: number;
  isValidForIndexation: boolean;
} {
  if (pageQuery === undefined) {
    return { fetchPage: 1, isValidForIndexation: true };
  }

  if (pageQuery === null || typeof pageQuery !== 'string') {
    return { fetchPage: 1, isValidForIndexation: false };
  }

  if (!/^[1-9]\d*$/.test(pageQuery)) {
    return { fetchPage: 1, isValidForIndexation: false };
  }

  const page = Number.parseInt(pageQuery, 10);

  if (page > 5) {
    return { fetchPage: page, isValidForIndexation: false };
  }

  return { fetchPage: page, isValidForIndexation: true };
}

function isLocationSetIndexable(locations: GeoLocation[] | null | undefined): boolean {
  if (!locations) {
    return false;
  }

  if (locations.length === 0) {
    return true;
  }

  if (locations.length > 1) {
    return false;
  }

  return isNewSrpLocationTypeIndexable(locations[0].type);
}

/** Pure indexation evaluator for Agency and Agent SRP pages. */
export function evaluateNewSrpIndexation({
  locations,
  pageQuery,
  totalCount,
}: NewSrpIndexationInput): NewSrpIndexationResult {
  const { fetchPage, isValidForIndexation } = parseDirectoryPageQuery(pageQuery);
  const passesLocationRules = isLocationSetIndexable(locations);
  const passesStructuralRules = passesLocationRules && isValidForIndexation;
  const hasResults = totalCount >= 1;
  const isIndexableForJsonLd =
    passesStructuralRules && hasResults && (locations?.length ?? 0) === 1;
  const isIndexable = passesStructuralRules && hasResults;

  let robotsContent: NewSrpRobotsContent | null = null;

  if (!passesStructuralRules) {
    robotsContent = 'noindex, nofollow';
  } else if (!hasResults) {
    robotsContent = 'noindex, follow';
  }

  return {
    isIndexable,
    isIndexableForJsonLd,
    robotsContent,
    fetchPage,
  };
}
